// @ts-nocheck
import { useState } from "react"
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { useComments } from "@/lib/hooks/useComments"
import { Avatar } from "@/components/ui/Avatar"
import { supabaseClient } from "@/lib/supabase/client"

interface Props { postId: string; userId: string | null }

export function CommentSection({ postId, userId }: Props) {
  const { comments, loading, submitting, addComment } = useComments(postId, userId)
  const [text, setText] = useState("")
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyText, setReplyText] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!text.trim()) return
    const ok = await addComment(text)
    if (ok) setText("")
  }

  const handleReply = async (parentId: string) => {
    if (!replyText.trim()) return
    const ok = await addComment(replyText, parentId)
    if (ok) { setReplyText(""); setReplyTo(null) }
  }

  const topLevel = comments.filter((c: any) => !c.parent_id)
  const getReplies = (id: string) => comments.filter((c: any) => c.parent_id === id)

  return (
    <div className="mt-4">
      <h3 className="font-syne font-bold text-sm uppercase tracking-wider mb-3">
        {comments.length} Comment{comments.length !== 1 ? "s" : ""}
      </h3>

      {/* Composer */}
      {userId ? (
        <form onSubmit={handleSubmit} className="mb-4">
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="What are your thoughts?"
            rows={3}
            className="sq-input resize-none mb-2"
          />
          <button type="submit" disabled={submitting || !text.trim()} className="btn-primary text-xs px-4 py-1.5">
            {submitting ? "Posting..." : "Comment"}
          </button>
        </form>
      ) : (
        <div className="panel p-4 mb-4 text-sm text-center text-muted-fg">
          <Link to="/auth/login" className="text-brand hover:underline font-semibold">Log in</Link> to comment
        </div>
      )}

      {/* Comments */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded" />)}
        </div>
      ) : (
        <div className="space-y-3">
          {topLevel.map((c: any) => (
            <div key={c.id}>
              <CommentItem comment={c} userId={userId}
                onReply={() => setReplyTo(replyTo === c.id ? null : c.id)} />
              {/* Replies */}
              {getReplies(c.id).map((r: any) => (
                <div key={r.id} className="ml-6 mt-2 pl-3 border-l-2 border-border">
                  <CommentItem comment={r} userId={userId} onReply={() => {}} />
                </div>
              ))}
              {/* Reply composer */}
              <AnimatePresence>
                {replyTo === c.id && userId && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                    className="ml-6 mt-2 pl-3 border-l-2 border-brand">
                    <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                      placeholder={`Reply to u/${c.users?.username || "user"}...`}
                      rows={2} className="sq-input resize-none mb-2 text-sm" />
                    <div className="flex gap-2">
                      <button onClick={() => handleReply(c.id)} disabled={submitting || !replyText.trim()}
                        className="btn-primary text-xs px-3 py-1">Reply</button>
                      <button onClick={() => setReplyTo(null)} className="btn-ghost text-xs">Cancel</button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
          {topLevel.length === 0 && !loading && (
            <div className="panel p-8 text-center text-sm text-muted-fg">
              No comments yet. Be the first to share your thoughts.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CommentItem({ comment: c, userId, onReply }: any) {
  const author = c.users
  const timeAgo = (ts: string) => {
    const d = (Date.now() - new Date(ts).getTime()) / 1000
    if (d < 60) return "just now"
    if (d < 3600) return Math.floor(d/60) + "m"
    if (d < 86400) return Math.floor(d/3600) + "h"
    return Math.floor(d/86400) + "d"
  }

  return (
    <div className="flex gap-2.5">
      <Avatar src={author?.avatar_url} alt={author?.display_name || "?"} size="sm" className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Link to={`/profile/${author?.username}`} className="text-xs font-bold hover:text-brand transition-colors">
            u/{author?.username || "deleted"}
          </Link>
          <span className="text-xs text-muted-fg">{timeAgo(c.created_at)}</span>
        </div>
        <p className="text-sm leading-relaxed text-foreground mb-1.5">{c.content}</p>
        <div className="flex items-center gap-1">
          {userId && (
            <button onClick={onReply} className="action-btn text-xs">Reply</button>
          )}
        </div>
      </div>
    </div>
  )
}
